import type { FrontendModule } from '@aurora/module-sdk/web';

/**
 * Kommandos der Command-Palette (⌘K). Reine Datenstruktur + pure Funktionen —
 * dadurch unabhängig von React testbar.
 */
export interface Command {
  id: string;
  title: string;
  section: string;
  keywords: readonly string[];
  /** Navigation (to) ODER Aktion (run). */
  to?: string;
  run?: () => void;
}

export interface BuildCommandsOptions {
  frontendModules: readonly FrontendModule[];
  onToggleTheme: () => void;
  onLogout: () => void;
}

export function buildCommands(options: BuildCommandsOptions): Command[] {
  const commands: Command[] = [
    {
      id: 'core.dashboard',
      title: 'Dashboard öffnen',
      section: 'Navigation',
      keywords: ['home', 'start', 'übersicht'],
      to: '/',
    },
    {
      id: 'core.analytics',
      title: 'Auswertungen öffnen',
      section: 'Navigation',
      keywords: ['report', 'bericht', 'auswertung', 'analyse', 'export', 'excel'],
      to: '/auswertungen',
    },
    {
      id: 'core.modules',
      title: 'Module verwalten',
      section: 'Navigation',
      keywords: ['einstellungen', 'apps', 'aktivieren'],
      to: '/einstellungen/module',
    },
    {
      id: 'core.settings',
      title: 'Stammdaten pflegen',
      section: 'Navigation',
      keywords: ['briefkopf', 'firma', 'steuernummer', 'bank', 'einstellungen'],
      to: '/einstellungen/stammdaten',
    },
    {
      id: 'core.users',
      title: 'Benutzer verwalten',
      section: 'Navigation',
      keywords: ['einladen', 'rollen', 'team', 'mitglieder'],
      to: '/einstellungen/benutzer',
    },
    {
      id: 'core.theme',
      title: 'Design umschalten (Hell/Dunkel)',
      section: 'Aktionen',
      keywords: ['theme', 'dark', 'light', 'dunkel', 'hell'],
      run: options.onToggleTheme,
    },
    {
      id: 'core.logout',
      title: 'Abmelden',
      section: 'Aktionen',
      keywords: ['logout', 'sign out'],
      run: options.onLogout,
    },
  ];

  for (const frontendModule of options.frontendModules) {
    for (const item of frontendModule.navItems) {
      commands.push({
        id: `${frontendModule.name}.nav.${item.to}`,
        title: item.label,
        section: frontendModule.title,
        keywords: [frontendModule.name, frontendModule.title.toLowerCase()],
        to: item.to,
      });
    }
    for (const command of frontendModule.commands ?? []) {
      commands.push({
        id: command.id,
        title: command.title,
        section: frontendModule.title,
        keywords: command.keywords ?? [],
        ...(command.to !== undefined ? { to: command.to } : {}),
      });
    }
  }

  return commands;
}

/** Einfache, robuste Filterung: jeder Suchbegriff muss in Titel/Keywords vorkommen. */
export function filterCommands(commands: readonly Command[], query: string): Command[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [...commands];
  return commands.filter((command) => {
    const haystack = [command.title, command.section, ...command.keywords].join(' ').toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}
